import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, useLoadScript, Marker, Polyline, InfoWindow, OverlayView } from '@react-google-maps/api';
import {
  Alert,
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { VERDICT_COLORS, VERDICT_LEGEND, formatLinkArrowLines, formatTxChange, getVerdictLabel } from '../../utils/powerMapUtils';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['geometry', 'marker'];

const defaultCenter = { lat: 32.0853, lng: 34.7818 };

const SHOW_MODE_LABELS = {
  all: 'All links',
  actionable: 'Actionable',
  weak_rssi: 'Weak RSSI',
};

const LinkArrowLabel = ({ link, isSelected, onClick }) => {
  const { staLine, apLine } = formatLinkArrowLines(link);
  const tooltip = (
    <Box sx={{ textAlign: 'left', fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.45 }}>
      <Typography variant="caption" display="block" sx={{ fontWeight: 700, mb: 0.25 }}>
        {link.childSerial} → {link.parentSerial}
        {link.band ? ` · ${link.band}` : ''}
      </Typography>
      <Typography variant="caption" display="block">{staLine}</Typography>
      <Typography variant="caption" display="block">{apLine}</Typography>
      <Typography variant="caption" display="block" sx={{ mt: 0.25, opacity: 0.85 }}>
        STA uplink · RSSI at parent / AP downlink · RSSI at child
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tooltip} arrow placement="top" enterDelay={250}>
      <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') onClick?.();
        }}
        sx={{
          position: 'relative',
          transform: 'translate(-50%, calc(-50% - 8px))',
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 118,
          px: 0.75,
          py: 0.4,
          borderRadius: '4px',
          bgcolor: 'rgba(255, 255, 255, 0.96)',
          border: '1.5px solid',
          borderColor: link.color,
          boxShadow: isSelected
            ? `0 0 0 2px ${link.color}, 0 2px 8px rgba(0,0,0,0.35)`
            : '0 1px 4px rgba(0,0,0,0.28)',
          pointerEvents: 'auto',
          cursor: 'pointer',
          userSelect: 'none',
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        <Typography
          component="span"
          display="block"
          sx={{ fontFamily: 'monospace', fontSize: '0.62rem', fontWeight: 700, color: '#111', lineHeight: 1.35 }}
        >
          {staLine}
        </Typography>
        <Typography
          component="span"
          display="block"
          sx={{ fontFamily: 'monospace', fontSize: '0.62rem', fontWeight: 700, color: '#333', lineHeight: 1.35 }}
        >
          {apLine}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const PowerMapView = ({
  devices,
  mapLinks,
  deviceMarkers,
  selectedLinkId,
  onSelectLink,
  height = 520,
  showMode,
  onShowModeChange,
}) => {
  const theme = useTheme();
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const mapColorScheme = useMemo(() => {
    if (!isLoaded || !window.google?.maps?.ColorScheme) return undefined;
    return theme.palette.mode === 'dark'
      ? window.google.maps.ColorScheme.DARK
      : window.google.maps.ColorScheme.LIGHT;
  }, [isLoaded, theme.palette.mode]);

  const fitToContent = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    mapLinks.forEach((link) => {
      bounds.extend({ lat: link.childLat, lng: link.childLng });
      bounds.extend({ lat: link.parentLat, lng: link.parentLng });
      hasPoints = true;
    });

    if (!hasPoints) {
      deviceMarkers.forEach((dm) => {
        bounds.extend({ lat: dm.lat, lng: dm.lng });
        hasPoints = true;
      });
    }

    if (hasPoints) {
      map.fitBounds(bounds, 48);
    }
  }, [mapLinks, deviceMarkers]);

  const handleMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      fitToContent();
    },
    [fitToContent],
  );

  useEffect(() => {
    fitToContent();
  }, [fitToContent]);

  const handleShowModeChange = (_event, value) => {
    if (value) onShowModeChange?.(value);
  };

  const selectedLink = useMemo(
    () => (selectedLinkId ? mapLinks.find((l) => l.id === selectedLinkId) : null),
    [selectedLinkId, mapLinks],
  );

  const selectedLinkMidpoint = useMemo(() => {
    if (!selectedLink) return null;
    return {
      lat: (selectedLink.childLat + selectedLink.parentLat) / 2,
      lng: (selectedLink.childLng + selectedLink.parentLng) / 2,
    };
  }, [selectedLink]);

  const mapCenter = useMemo(() => {
    if (mapLinks.length) {
      let latSum = 0;
      let lngSum = 0;
      let count = 0;
      mapLinks.forEach((link) => {
        latSum += link.childLat + link.parentLat;
        lngSum += link.childLng + link.parentLng;
        count += 2;
      });
      return { lat: latSum / count, lng: lngSum / count };
    }
    if (deviceMarkers.length) {
      const latSum = deviceMarkers.reduce((sum, dm) => sum + dm.lat, 0);
      const lngSum = deviceMarkers.reduce((sum, dm) => sum + dm.lng, 0);
      return { lat: latSum / deviceMarkers.length, lng: lngSum / deviceMarkers.length };
    }
    return defaultCenter;
  }, [mapLinks, deviceMarkers]);

  if (loadError) {
    return <Alert severity="error">Failed to load Google Maps.</Alert>;
  }

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height, borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 2,
        }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={showMode}
          onChange={handleShowModeChange}
          aria-label="Power map link filter"
        >
          {Object.entries(SHOW_MODE_LABELS).map(([mode, label]) => (
            <ToggleButton key={mode} value={mode} sx={{ px: 1.25, py: 0.5, fontSize: '0.75rem' }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 10,
          left: 10,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 2,
          px: 1,
          py: 0.75,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          maxWidth: 'min(420px, calc(100% - 20px))',
        }}
      >
        {VERDICT_LEGEND.map(({ key, label }) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: VERDICT_COLORS[key] || VERDICT_COLORS.missing_data,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" sx={{ lineHeight: 1.2 }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {!mapLinks.length && !deviceMarkers.length ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Typography color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
            No backhaul links with GPS data to display. Ensure devices have location data and active backhaul connections.
          </Typography>
        </Box>
      ) : (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={12}
          onLoad={handleMapLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true,
            mapId: 'RRM_POWER_MAP',
            colorScheme: mapColorScheme,
          }}
        >
          {deviceMarkers.map((dm) => {
            const serialLower = dm.serial.toLowerCase();
            const isSelected = selectedLink &&
              (selectedLink.childSerial === serialLower || selectedLink.parentSerial === serialLower);
            return (
              <Marker
                key={dm.serial}
                position={{ lat: dm.lat, lng: dm.lng }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: dm.isCap ? 7 : 5,
                  fillColor: isSelected ? '#1976d2' : (dm.connected ? '#424242' : '#9e9e9e'),
                  fillOpacity: dm.connected ? 0.8 : 0.3,
                  strokeColor: isSelected ? '#0d47a1' : '#333',
                  strokeWeight: isSelected ? 2 : 1,
                }}
              />
            );
          })}

          {mapLinks.map((link) => {
            const isSelected = link.id === selectedLinkId;
            const midLat = (link.childLat + link.parentLat) / 2;
            const midLng = (link.childLng + link.parentLng) / 2;
            return (
              <React.Fragment key={link.id}>
                <Polyline
                  path={[
                    { lat: link.childLat, lng: link.childLng },
                    { lat: link.parentLat, lng: link.parentLng },
                  ]}
                  options={{
                    strokeColor: link.color,
                    strokeWeight: isSelected ? 5 : (link.hasAction ? 3 : 2),
                    strokeOpacity: isSelected ? 1.0 : (link.hasAction ? 0.85 : 0.55),
                    clickable: true,
                    icons: [{
                      icon: {
                        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: isSelected ? 4 : 3,
                        strokeColor: link.color,
                        fillColor: link.color,
                        fillOpacity: 1,
                        strokeWeight: 1,
                      },
                      offset: '85%',
                    }],
                  }}
                  onClick={() => onSelectLink?.(link.id)}
                />
                <OverlayView
                  position={{ lat: midLat, lng: midLng }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <LinkArrowLabel
                    link={link}
                    isSelected={isSelected}
                    onClick={() => onSelectLink?.(link.id)}
                  />
                </OverlayView>
              </React.Fragment>
            );
          })}

          {selectedLink && selectedLinkMidpoint && (
            <InfoWindow
              position={selectedLinkMidpoint}
              onCloseClick={() => onSelectLink?.(null)}
            >
              <Box sx={{ minWidth: 260, color: '#000', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {selectedLink.childSerial} → {selectedLink.parentSerial}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                  Band: {selectedLink.band}
                  {selectedLink.apRadioName ? ` · ${selectedLink.apRadioName}` : ''}
                </Typography>

                <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', lineHeight: 1.6 }}>
                  ↑ STA (uplink): {formatTxChange(selectedLink.staTxCurrent, selectedLink.staTxRecommended)}
                  &nbsp;&nbsp;|&nbsp;&nbsp;RSSI at parent: {selectedLink.rssiAtParent != null ? `${selectedLink.rssiAtParent} dBm` : '—'}
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', lineHeight: 1.6 }}>
                  ↓ AP (downlink): {formatTxChange(selectedLink.apTxCurrent, selectedLink.apTxRecommended)}
                  &nbsp;&nbsp;|&nbsp;&nbsp;RSSI at child: {selectedLink.rssiAtChild != null ? `${selectedLink.rssiAtChild} dBm` : '—'}
                </Typography>

                <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 0.5,
                      borderRadius: 0.5,
                      bgcolor: (VERDICT_COLORS[selectedLink.staVerdict] || VERDICT_COLORS.missing_data) + '22',
                      color: VERDICT_COLORS[selectedLink.staVerdict] || VERDICT_COLORS.missing_data,
                      fontWeight: 600,
                    }}
                  >
                    STA: {getVerdictLabel(selectedLink.staVerdict)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 0.5,
                      borderRadius: 0.5,
                      bgcolor: (VERDICT_COLORS[selectedLink.apVerdict] || VERDICT_COLORS.missing_data) + '22',
                      color: VERDICT_COLORS[selectedLink.apVerdict] || VERDICT_COLORS.missing_data,
                      fontWeight: 600,
                    }}
                  >
                    AP: {getVerdictLabel(selectedLink.apVerdict)}
                  </Typography>
                </Box>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </Box>
  );
};

export default PowerMapView;
